import { Button, Card, DatePicker, message, Spin, Typography, Tabs, Table, Space } from "antd";
import { useEffect, useState } from "react";
import api from "../../api/axios";
import ShareEsimModal from "./ShareEsimModal";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type TopupRow = {
  id: number;
  resellerid: number;
  resellername?: string | null;
  amount: number;
  method?: string | null;
  reference?: string | null;
  note?: string | null;
  createdby_userid?: number | null;
  createdby?: string | null;
  createdat: string; // ISO
};

type SaleRow = {
  id: number;
  resellerid: number;
  packageid: string;
  price: number;
  email?: string | null;
  buyer_email?: string | null;
  buyerEmail?: string | null;
  display_email?: string | null;
  displayEmail?: string | null;
  buyer_type?: string | null;
  buyerType?: string | null;
  entrydate: string; // ISO
  generatedid: string;
};

type UpayOrderRow = {
  id: number;
  local_order_id: string;
  customer_email?: string | null;
  amount: number;
  payment_status?: string | null;
  delivery_status?: string | null;
  created_at?: string | null;
};

export default function ResellerBalance() {
  const [balance, setBalance] = useState<number | null>(null);

  const [loadingBalance, setLoadingBalance] = useState(false);
  const [loadingTopups, setLoadingTopups] = useState(false);
  const [loadingSales, setLoadingSales] = useState(false);

  const [topups, setTopups] = useState<TopupRow[]>([]);
  const [sales, setSales] = useState<SaleRow[]>([]);
  const [loadingUpayOrders, setLoadingUpayOrders] = useState(false);
  const [upayOrders, setUpayOrders] = useState<UpayOrderRow[]>([]);
  const [activeTab, setActiveTab] = useState("balance");
  const [salesRange, setSalesRange] = useState<[any, any] | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareSaleId, setShareSaleId] = useState<number | null>(null);

  const topupsTotal = topups.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const salesTotal = sales.reduce((sum, row) => sum + Number(row.price || 0), 0);

  const formatAmount = (value: number | null, loading: boolean) => {
    if (loading) return "...";
    if (typeof value !== "number" || Number.isNaN(value)) return "-";
    return `${value.toFixed(2)}€`;
  };

  const formatCurrency = (value: number | null) => {
    if (typeof value !== "number" || Number.isNaN(value)) return "-";
    return `${value.toFixed(2)}€`;
  };

  const escapeCsvValue = (value: unknown) => {
    if (value == null) return "";
    const stringValue = String(value);
    if (/[",\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, "\"\"")}"`;
    }
    return stringValue;
  };

  const formatRangeLabel = (value: any) => {
    if (!value || typeof value.toISOString !== "function") return "";
    return value.toISOString().slice(0, 10);
  };

  const normalizeEmail = (value?: string | null) => (value || "").trim();

  const getDisplayEmail = (row: SaleRow) => {
    const primary = normalizeEmail(row.email);
    if (primary) return primary;
    return normalizeEmail(
      row.displayEmail ??
        row.display_email ??
        row.buyerEmail ??
        row.buyer_email ??
        null
    );
  };

  const exportSalesCsv = () => {
    if (!sales.length) {
      message.info("No sales to export.");
      return;
    }

    const headers = ["ID", "Price", "Package ID", "Email", "Date", "Generated ID"];
    const rows = sales.map((row) => [
      row.id,
      Number(row.price).toFixed(2),
      row.packageid,
      getDisplayEmail(row),
      row.entrydate ? new Date(row.entrydate).toISOString() : "",
      row.generatedid,
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n");

    const fromLabel = formatRangeLabel(salesRange?.[0]);
    const toLabel = formatRangeLabel(salesRange?.[1]);
    const rangeSuffix =
      fromLabel || toLabel ? `-${fromLabel || "start"}-to-${toLabel || "end"}` : "";
    const fileName = `sales${rangeSuffix}.csv`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const fetchBalance = async () => {
    setLoadingBalance(true);
    try {
      const response = await api.get("/pos/Resellers/getBalance");
      setBalance(response.data);
      message.success("Balance loaded.");
    } catch (error) {
      message.error("Failed to load balance.");
    } finally {
      setLoadingBalance(false);
    }
  };

  const fetchTopups = async () => {
    setLoadingTopups(true);
    try {
      const res = await api.get("/pos/Resellers/topups", { params: { limit: 25, offset: 0 } });
      setTopups(res.data || []);
    } catch {
      message.error("Failed to load topups (credit).");
    } finally {
      setLoadingTopups(false);
    }
  };

  const fetchSales = async (range = salesRange) => {
    setLoadingSales(true);
    try {
      const params: any = { limit: 25, offset: 0 };
      if (range?.[0]) params.from = range[0].toISOString();
      if (range?.[1]) params.to = range[1].toISOString();
      const res = await api.get("/pos/Resellers/sales", { params });
      setSales(res.data || []);
    } catch {
      message.error("Failed to load sales (debit).");
    } finally {
      setLoadingSales(false);
    }
  };

  const fetchUpayOrders = async () => {
    setLoadingUpayOrders(true);
    try {
      const res = await api.get('/pos/Resellers/upay-orders', { params: { limit: 50, offset: 0 } });
      setUpayOrders(res.data || []);
    } catch {
      message.error('Failed to load UPay orders.');
    } finally {
      setLoadingUpayOrders(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([fetchBalance(), fetchTopups(), fetchSales(), fetchUpayOrders()]);
  };

  const handleSalesRangeChange = (value: any) => {
    setSalesRange(value as any);
    fetchSales(value as any);
  };

  const openShareModal = (saleId: number) => {
    setShareSaleId(saleId);
    setShareOpen(true);
  };

  const closeShareModal = () => {
    setShareOpen(false);
    setShareSaleId(null);
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const topupColumns = [
    { title: "ID", dataIndex: "id", key: "id", width: 80 },
    {
      title: "Amount (Credit)",
      dataIndex: "amount",
      key: "amount",
      render: (v: number) => <b>{formatCurrency(Number(v))}</b>,
      width: 140,
    },
    { title: "Method", dataIndex: "method", key: "method", width: 120 },
    { title: "Reference", dataIndex: "reference", key: "reference", width: 160 },
    { title: "Note", dataIndex: "note", key: "note" },
    { title: "By", dataIndex: "createdby", key: "createdby", width: 120 },
    {
      title: "Date",
      dataIndex: "createdat",
      key: "createdat",
      width: 220,
      render: (v: string) => (v ? new Date(v).toLocaleString() : ""),
    },
  ];

  const salesColumns = [
    { title: "ID", dataIndex: "id", key: "id", width: 80 },
    {
      title: "Price (Debit)",
      dataIndex: "price",
      key: "price",
      render: (v: number) => <b>{formatCurrency(Number(v))}</b>,
      width: 140,
    },
    { title: "Package ID", dataIndex: "packageid", key: "packageid", width: 140 },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 220,
      render: (_: string, row: SaleRow) => getDisplayEmail(row),
    },
    {
      title: "Date",
      dataIndex: "entrydate",
      key: "entrydate",
      width: 220,
      render: (v: string) => (v ? new Date(v).toLocaleString() : ""),
    },
    { title: "Generated ID", dataIndex: "generatedid", key: "generatedid" },
    {
      title: "Action",
      key: "action",
      width: 140,
      render: (_: any, row: SaleRow) => (
        <Button type="link" onClick={() => openShareModal(row.id)}>
          Share eSIM
        </Button>
      ),
    },
  ];

  const upayColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Order ID', dataIndex: 'local_order_id', key: 'local_order_id', width: 220 },
    {
      title: 'Email',
      dataIndex: 'customer_email',
      key: 'customer_email',
      width: 220,
      render: (v: string | null | undefined) => v || '-',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 140,
      render: (v: number) => <b>{formatCurrency(Number(v))}</b>,
    },
    { title: 'Payment', dataIndex: 'payment_status', key: 'payment_status', width: 130 },
    { title: 'Delivery', dataIndex: 'delivery_status', key: 'delivery_status', width: 130 },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 220,
      render: (v: string) => (v ? new Date(v).toLocaleString() : ''),
    },
  ];

  const tabBarExtraContent =
    activeTab === "credit"
      ? {
          right: (
            <Space size={6} align="baseline">
              <Text type="secondary">Total Topups</Text>
              <Text strong>{formatAmount(topupsTotal, loadingTopups)}</Text>
            </Space>
          ),
        }
      : activeTab === "debit"
      ? {
          right: (
            <Space size={6} align="baseline">
              <Text type="secondary">Total sales</Text>
              <Text strong>{formatAmount(salesTotal, loadingSales)}</Text>
            </Space>
          ),
        }
      : undefined;

  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
      <Card
        bordered={false}
        style={{
          width: 1120,
          maxWidth: "100%",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          borderRadius: 12,
        }}
      >
        <Space style={{ width: "100%", justifyContent: "space-between" }} align="center">
          <Title level={3} style={{ margin: 0 }}>
            Transactions
          </Title>

          <Space size={12} wrap align="center">
            {activeTab === "debit" && (
              <>
                <RangePicker
                  showTime
                  allowClear
                  value={salesRange as any}
                  onChange={handleSalesRangeChange}
                />
                <Button onClick={exportSalesCsv} disabled={!sales.length}>
                  Export CSV
                </Button>
              </>
            )}
            <Button onClick={refreshAll} loading={loadingBalance || loadingTopups || loadingSales || loadingUpayOrders}>
              Refresh
            </Button>
          </Space>
        </Space>

        <div style={{ marginTop: 16 }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            tabBarExtraContent={tabBarExtraContent}
            items={[
              {
                key: "balance",
                label: "Balance",
                children: (
                  <div style={{ paddingTop: 12 }}>
                    {loadingBalance ? (
                      <Spin />
                    ) : (
                      <Text style={{ fontSize: 34, color: "#52c41a", fontWeight: "bold" }}>
                        {formatCurrency(balance)}
                      </Text>
                    )}
                    <div style={{ marginTop: 6, opacity: 0.7 }}>
                      Tip: Balance is live from DB. Credit = topups. Debit = sales.
                    </div>
                  </div>
                ),
              },
              {
                key: "credit",
                label: "Credit (Topups)",
                children: (
                  <Table
                    rowKey="id"
                    loading={loadingTopups}
                    columns={topupColumns as any}
                    dataSource={topups}
                    pagination={false}
                    style={{ marginTop: 10 }}
                    scroll={{ x: 900 }}
                  />
                ),
              },
              {
                key: "debit",
                label: "Debit (Sales)",
                children: (
                  <Table
                    rowKey="id"
                    loading={loadingSales}
                    columns={salesColumns as any}
                    dataSource={sales}
                    pagination={false}
                    style={{ marginTop: 10 }}
                    scroll={{ x: 900 }}
                  />
                ),
              },
              {
                key: 'upay',
                label: 'UPay Orders',
                children: (
                  <Table
                    rowKey="id"
                    loading={loadingUpayOrders}
                    columns={upayColumns as any}
                    dataSource={upayOrders}
                    pagination={false}
                    style={{ marginTop: 10 }}
                    scroll={{ x: 900 }}
                  />
                ),
              },
            ]}
          />
        </div>
      </Card>

      <ShareEsimModal
        open={shareOpen}
        saleId={shareSaleId}
        onClose={closeShareModal}
        onDelivered={fetchSales}
      />
    </div>
  );
}
